import { create } from 'zustand';
import { Notification } from '@services/notificationService';

interface NotificationState {
    // Notification state
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    currentPage: number;
    hasMore: boolean;

    // Actions
    setNotifications: (notifications: Notification[], hasMore: boolean) => void;
    appendNotifications: (notifications: Notification[], hasMore: boolean) => void;
    setUnreadCount: (count: number) => void;
    setLoading: (loading: boolean) => void;
    setCurrentPage: (page: number) => void;

    // Optimistic updates
    markAsRead: (notificationId: string) => void;
    markAsUnread: (notificationId: string) => void;
    markAllAsRead: () => void;
    removeNotification: (notificationId: string) => void;
    decrementUnreadCount: () => void;

    // Fetch helpers
    fetchUnreadCount: () => Promise<void>;

    // Reset
    reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    // Initial state
    notifications: [],
    unreadCount: 0,
    loading: false,
    currentPage: 1,
    hasMore: true,

    // Actions
    setNotifications: (notifications, hasMore) =>
        set({ notifications, hasMore, currentPage: 1 }),

    appendNotifications: (notifications, hasMore) =>
        set((state) => ({
            notifications: [...state.notifications, ...notifications],
            hasMore,
        })),

    setUnreadCount: (count) => set({ unreadCount: count }),

    setLoading: (loading) => set({ loading }),

    setCurrentPage: (page) => set({ currentPage: page }),

    // Optimistic update: mark single notification as read
    markAsRead: (notificationId) =>
        set((state) => {
            const notification = state.notifications.find(n => n.id === notificationId);
            const wasUnread = notification && !notification.read;

            return {
                notifications: state.notifications.map((n) =>
                    n.id === notificationId ? { ...n, read: true } : n
                ),
                unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            };
        }),

    // Optimistic update: mark single notification as unread
    markAsUnread: (notificationId) =>
        set((state) => {
            const notification = state.notifications.find(n => n.id === notificationId);
            const wasRead = notification && notification.read;

            return {
                notifications: state.notifications.map((n) =>
                    n.id === notificationId ? { ...n, read: false } : n
                ),
                unreadCount: wasRead ? state.unreadCount + 1 : state.unreadCount,
            };
        }),

    // Optimistic update: mark all as read
    markAllAsRead: () =>
        set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
            unreadCount: 0,
        })),

    // Remove notification
    removeNotification: (notificationId) =>
        set((state) => {
            const notification = state.notifications.find(n => n.id === notificationId);
            const wasUnread = notification && !notification.read;

            return {
                notifications: state.notifications.filter((n) => n.id !== notificationId),
                unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            };
        }),

    // Decrement unread count
    decrementUnreadCount: () =>
        set((state) => ({
            unreadCount: Math.max(0, state.unreadCount - 1),
        })),

    // Fetch unread count from API
    fetchUnreadCount: async () => {
        try {
            const { notificationService } = await import('@services/notificationService');
            const response = await notificationService.getUnreadCount();
            if (response.success) {
                set({ unreadCount: response.count });
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    },

    // Reset store
    reset: () =>
        set({
            notifications: [],
            unreadCount: 0,
            loading: false,
            currentPage: 1,
            hasMore: true,
        }),
}));
